import { __awaiter } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { PaymentGroupContent } from './PaymentGroupContent';
import { Poller } from '@as-react/common-utils';
import { DELAY } from '../../../../constants/GeneralConstants';
import { PaymentGroupName, SaferpayAltStatusResult, SaferpayFieldsBancontactAppOptionsState, } from '@as-react/common-types';
import { useDispatch, useSelector } from 'react-redux';
import { analyticsActions } from '../../../../redux/analytics/actions/AnalyticsActions.creators';
import { checkoutMutations, checkoutQueries } from '@as-react/common-queries';
import { useAemConfig } from '@as-react/common-components';
import { useMutation } from '@tanstack/react-query';
import { EventEnum } from '@as-react/common-types';
import { EventUtil } from '@as-react/common-utils';
import AsNumeral from '../../../../vendors/asNumeral';
import { queryClient } from '@as-react/common-queries';
import { makeGetBasketPricingState } from '../../../../redux/basket/selectors/BasketSelector';
import { useSaferpay } from '../../../../hooks/useSaferpay';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useTranslation } from 'react-i18next';
import { BancontactForm } from './forms/BancontactForm';
import { usePaymentState } from '../../../../context/CheckoutPaymentContext';
import { Logger } from '@as-react/logger';
import { ScrollUtil } from '../../../../util/ScrollUtil';
import { isMobileDevice } from '../../../../util/DeviceUtil';
const MAX_BASKET_TOTAL_FOR_APP = 500;
const BANCONTACT_PAYMENT_METHODS = ['bancontact'];
const ANALYTICS_PAYMENT_METHOD_QR = 'Bancontact QR Saferpay';
let timers = [];
export const PaymentGroupBancontact = ({ disabled, selected, group }) => {
    const paymentGroupContentRef = useRef();
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const [isTimerRun, setIsTimerRun] = useState(false);
    const [hasAppOptions, setHasAppOptions] = useState(false);
    const [appOptionsState, setAppOptionsState] = useState(SaferpayFieldsBancontactAppOptionsState.LOADING);
    const [qrCodeImage, setQrCodeImage] = useState('');
    const [intentUrl, setIntentUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showError, setShowError] = useState(false);
    const { defaultRequestParams, servicesEndpoint, currency } = useAemConfig();
    const { hasError: hasSaferpayError, validation } = useSaferpay({
        paymentMethods: BANCONTACT_PAYMENT_METHODS,
        enabled: selected,
    });
    const [{ optionError }] = usePaymentState();
    const { priceTotalBasket } = useSelector(makeGetBasketPricingState());
    const optionSelectError = optionError[group.name];
    const dispatch = useDispatch();
    const endTimers = () => {
        timers.forEach(timer => timer.end());
        timers = [];
    };
    const { getSaferpayAltStatus } = checkoutQueries({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { postSaferpayAlt, postSaferpayToken } = checkoutMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutate: mutatePostSaferpayAlt } = useMutation(postSaferpayAlt);
    const { mutate: mutatePostSaferpayToken } = useMutation(postSaferpayToken);
    const startAppPaymentTimer = (expiration) => {
        const expirationDate = new Date(expiration).getTime();
        const paymentTimer = new Poller(() => {
            const currentTime = Date.now();
            if (currentTime > expirationDate) {
                return true;
            }
            return false;
        }, DELAY.DELAY_1000);
        paymentTimer.run().then(() => {
            if (paymentTimer && paymentTimer.active) {
                setAppOptionsState(SaferpayFieldsBancontactAppOptionsState.DISABLED);
            }
        });
        timers.push(paymentTimer);
        setIsTimerRun(true);
    };
    const startPollingForAppPayment = (requestId) => {
        const paymentPoller = new Poller(() => __awaiter(void 0, void 0, void 0, function* () {
            const result = yield queryClient.fetchQuery(getSaferpayAltStatus(requestId, {
                staleTime: 0,
            }));
            if ((result === null || result === void 0 ? void 0 : result.status) &&
                (result.status === SaferpayAltStatusResult.CAPTURED || result.status === SaferpayAltStatusResult.FAILURE)) {
                dispatch(analyticsActions.paymentRequest({
                    paymentGroup: PaymentGroupName.BANCONTACT_SAFERPAY,
                    paymentMethod: ANALYTICS_PAYMENT_METHOD_QR,
                }));
                return result.redirectUrl;
            }
            return;
        }), DELAY.DELAY_2000);
        paymentPoller
            .run()
            .then((redirectUrl) => {
            if (redirectUrl) {
                location.href = redirectUrl;
            }
        })
            .catch(({ message }) => {
            Logger.logError(`Saferpay-fields polling error ${message}`);
        });
        timers.push(paymentPoller);
        setIsTimerRun(true);
    };
    const initBancontactPayment = () => __awaiter(void 0, void 0, void 0, function* () {
        endTimers();
        setQrCodeImage('');
        setIntentUrl('');
        setAppOptionsState(SaferpayFieldsBancontactAppOptionsState.LOADING);
        mutatePostSaferpayAlt(isMobileDevice(), {
            onSuccess: result => {
                endTimers();
                startAppPaymentTimer(result.expiration);
                startPollingForAppPayment(result.requestId);
                setQrCodeImage(result.qrCodeImage);
                setIntentUrl(result.intentUrl);
                setAppOptionsState(SaferpayFieldsBancontactAppOptionsState.ENABLED);
            },
            onError: () => {
                setAppOptionsState(SaferpayFieldsBancontactAppOptionsState.DISABLED);
            },
        });
    });
    const handleSubmit = () => {
        setIsSubmitting(true);
        dispatch(analyticsActions.paymentRequest({
            paymentGroup: PaymentGroupName.BANCONTACT_SAFERPAY,
        }));
        window.SaferpayFields.submit({
            onSuccess: (_a) => __awaiter(void 0, [_a], void 0, function* ({ token }) {
                mutatePostSaferpayToken({ token }, {
                    onSuccess: ({ redirectUrl }) => {
                        if (redirectUrl) {
                            location.href = redirectUrl;
                        }
                    },
                    onError: () => {
                        setIsSubmitting(false);
                        setShowError(true);
                    },
                });
            }),
            onError: () => {
                ScrollUtil.scrollToElement(paymentGroupContentRef.current, true);
                setIsSubmitting(false);
            },
        });
    };
    const handleVoucherChange = () => {
        selected && hasAppOptions && initBancontactPayment();
    };
    useEffect(() => {
        if (selected && hasAppOptions) {
            initBancontactPayment();
        }
        else {
            endTimers();
        }
        EventUtil.listen(EventEnum.VOUCHER_CHANGED, handleVoucherChange);
        return () => {
            EventUtil.remove(EventEnum.VOUCHER_CHANGED, handleVoucherChange);
        };
    }, [selected, hasAppOptions]);
    useEffect(() => {
        return () => {
            isTimerRun && endTimers();
        };
    }, [isTimerRun]);
    useEffect(() => {
        setHasAppOptions(priceTotalBasket <= MAX_BASKET_TOTAL_FOR_APP);
    }, [priceTotalBasket]);
    return (_jsx(PaymentGroupContent, { group: group, selected: selected, onSubmit: handleSubmit, error: (optionSelectError !== null && optionSelectError !== void 0 ? optionSelectError : (!!hasSaferpayError || showError))
            ? t(`${I18N_NAMESPACES.buy}:buy.payment.form.error`)
            : undefined, disabled: disabled || !!hasSaferpayError || showError, isSubmitting: isSubmitting, innerRef: paymentGroupContentRef, children: selected && (_jsx(BancontactForm, { appOptionsState: appOptionsState, currency: currency, hasAppOptions: hasAppOptions, intentUrl: intentUrl, paymentMethods: BANCONTACT_PAYMENT_METHODS, priceTotalBasket: AsNumeral.formatAsCurrency(priceTotalBasket), qrCodeImage: qrCodeImage, validation: validation })) }));
};
